import React, { useState, useEffect, useCallback } from "react";
import {
  AlertCircle,
  CheckCircle,
  Plus,
  Eye,
  Trash2,
  Edit2,
  DollarSign,
  Loader,
  X,
  Tag,
  Clock,
  Upload,
} from "lucide-react";
import ExpiresAtPicker from "../components/ExpiresAtPicker";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

/** Safe for datetime-local / ExpiresAtPicker: local `YYYY-MM-DDTHH:mm` (no Invalid Date throws). */
function toLocalDatetimeLocalString(raw) {
  if (raw == null || raw === "") return "";
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const CouponsManagement = ({ userRole = "admin" }) => {
  const { isDarkMode } = useTheme();

  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [showCouponDetailModal, setShowCouponDetailModal] = useState(false);
  const [showDeleteCouponConfirm, setShowDeleteCouponConfirm] = useState(false);

  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);

  const initialFormState = {
    couponCode: "",
    title: "",
    description: "",
    imageUrl: "",
    coverImage: "",
    priceInTokens: "",
    discountType: "percentage",
    discountValue: "",
    expiresAt: "",
    minOrderValue: "",
    terms: "",
    applicableItems: "",
    type: "discount",
    isActive: true,
  };

  const [couponForm, setCouponForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});

  const getAuthHeader = () => {
    const token = localStorage.getItem("accessToken");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const parseApiResponse = async (response) => {
    const responseText = await response.text();

    if (!responseText) {
      return null;
    }

    try {
      return JSON.parse(responseText);
    } catch {
      if (
        responseText.trim().startsWith("<!DOCTYPE") ||
        responseText.trim().startsWith("<html")
      ) {
        throw new Error(
          "API returned HTML instead of JSON. Check REACT_APP_BASE_URL and backend availability.",
        );
      }
      throw new Error("Invalid JSON response from API");
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!couponForm.title?.trim()) {
      errors.title = "Title is required";
    } else if (couponForm.title.trim().length < 3) {
      errors.title = "Title must be at least 3 characters";
    }

    // Coupon code is only editable/required during creation.
    if (!isEditing) {
      if (!couponForm.couponCode?.trim()) {
        errors.couponCode = "Coupon code is required";
      } else if (!/^[A-Z0-9_-]{3,30}$/.test(couponForm.couponCode.trim())) {
        errors.couponCode =
          "Use 3-30 chars: uppercase letters, numbers, _ or -";
      }
    }

    if (!couponForm.description?.trim()) {
      errors.description = "Description is required";
    } else if (couponForm.description.trim().length < 10) {
      errors.description = "Description must be at least 10 characters";
    }

    if (!couponForm.imageUrl?.trim() && !imageFile) {
      errors.imageUrl = "Image is required";
    } else if (
      !imageFile &&
      couponForm.imageUrl?.trim() &&
      !isValidUrl(couponForm.imageUrl)
    ) {
      errors.imageUrl = "Invalid image URL";
    }

    if (!couponForm.coverImage?.trim() && !coverImageFile) {
      errors.coverImage = "Cover image is required";
    } else if (
      !coverImageFile &&
      couponForm.coverImage?.trim() &&
      !isValidUrl(couponForm.coverImage)
    ) {
      errors.coverImage = "Invalid cover image URL";
    }

    if (!couponForm.priceInTokens) {
      errors.priceInTokens = "Price in tokens is required";
    } else if (Number(couponForm.priceInTokens) < 1) {
      errors.priceInTokens = "Price must be at least 1 token";
    }

    if (!["percentage", "fixed"].includes(couponForm.discountType)) {
      errors.discountType = "Invalid discount type";
    }

    if (!couponForm.discountValue) {
      errors.discountValue = "Discount value is required";
    } else {
      const value = Number(couponForm.discountValue);
      if (value < 1) {
        errors.discountValue = "Discount value must be at least 1";
      }
      if (couponForm.discountType === "percentage" && value > 100) {
        errors.discountValue = "Percentage discount cannot exceed 100%";
      }
    }

    if (!couponForm.expiresAt) {
      errors.expiresAt = "Expiry date is required";
    } else {
      const expiryDate = new Date(couponForm.expiresAt);
      const now = new Date();
      if (expiryDate <= now) {
        errors.expiresAt = "Expiry date must be in the future";
      }
    }

    if (couponForm.minOrderValue && Number(couponForm.minOrderValue) < 1) {
      errors.minOrderValue = "Min order value must be at least 1";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const fetchCoupons = useCallback(async () => {
    try {
      setCouponsLoading(true);
      const response = await fetch(`${API_BASE_URL}/coupons`, {
        method: "GET",
        headers: getAuthHeader(),
      });

      const data = await parseApiResponse(response);

      if (!response.ok) {
        throw new Error(
          data?.message || `Failed to fetch coupons (${response.status})`,
        );
      }

      const couponsPayload = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const now = Date.now();
      setCoupons(
        couponsPayload.filter((coupon) => {
          if (coupon?.isDeleted === true || coupon?.deletedAt != null) {
            return false;
          }

          if (!coupon?.expiresAt) {
            return true;
          }

          const expiryTime = new Date(coupon.expiresAt).getTime();
          return Number.isFinite(expiryTime) ? expiryTime > now : true;
        }),
      );
    } catch (err) {
      setError(err.message || "Failed to fetch coupons");
    } finally {
      setCouponsLoading(false);
    }
  }, []);

  const openCreateModal = () => {
    setCouponForm(initialFormState);
    setIsEditing(false);
    setSelectedCoupon(null);
    setFormErrors({});
    setImageFile(null);
    setCoverImageFile(null);
    setImagePreview(null);
    setCoverImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (coupon) => {
    setCouponForm({
      couponCode: coupon.couponCode ?? "",
      title: coupon.title ?? "",
      description: coupon.description ?? "",
      imageUrl: coupon.imageUrl ?? "",
      coverImage: coupon.coverImage ?? "",
      priceInTokens:
        coupon.priceInTokens != null && coupon.priceInTokens !== ""
          ? String(coupon.priceInTokens)
          : "",
      discountType: coupon.discountType ?? "percentage",
      discountValue:
        coupon.discountValue != null && coupon.discountValue !== ""
          ? String(coupon.discountValue)
          : "",
      expiresAt: toLocalDatetimeLocalString(coupon.expiresAt),
      minOrderValue:
        coupon.minOrderValue != null && coupon.minOrderValue !== ""
          ? String(coupon.minOrderValue)
          : "",
      terms: coupon.terms ?? "",
      applicableItems: coupon.applicableItems?.join(", ") || "",
      type: coupon.type ?? "discount",
      isActive: coupon.isActive !== false,
    });
    setIsEditing(true);
    setSelectedCoupon(coupon);
    setFormErrors({});
    setImageFile(null);
    setCoverImageFile(null);
    setImagePreview(coupon.imageUrl || null);
    setCoverImagePreview(coupon.coverImage || null);
    setShowModal(true);
  };

  const handleFileChange = (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (kind === "image") {
        setImageFile(file);
        setImagePreview(reader.result);
      } else {
        setCoverImageFile(file);
        setCoverImagePreview(reader.result);
      }
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const uploadImageUsingPresigned = async (file, type = "coupons") => {
    const response = await fetch(`${API_BASE_URL}/uploads/presigned-url`, {
      method: "POST",
      headers: getAuthHeader(),
      body: JSON.stringify({
        fileName: file.name,
        type,
      }),
    });

    const presignedData = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(presignedData?.message || "Failed to get upload URL");
    }

    const { uploadUrl } = presignedData.data || {};
    if (!uploadUrl) {
      throw new Error("Upload URL not returned by server");
    }

    const uploadResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error("Failed to upload image");
    }

    return uploadUrl.split("?")[0];
  };

  const handleSaveCoupon = async () => {
    if (!validateForm()) {
      setError("Please fix the form errors below");
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      let imageUrl = couponForm.imageUrl.trim();
      let coverImageUrl = couponForm.coverImage.trim();

      if (imageFile) {
        setUploadingImage(true);
        imageUrl = await uploadImageUsingPresigned(imageFile, "coupons");
      }

      if (coverImageFile) {
        setUploadingCoverImage(true);
        coverImageUrl = await uploadImageUsingPresigned(coverImageFile, "coupons");
      }

      const payload = {
        title: couponForm.title.trim(),
        description: couponForm.description.trim(),
        imageUrl,
        coverImage: coverImageUrl,
        priceInTokens: Number(couponForm.priceInTokens),
        discountType: couponForm.discountType,
        discountValue: Number(couponForm.discountValue),
        expiresAt: couponForm.expiresAt,
        minOrderValue: couponForm.minOrderValue
          ? Number(couponForm.minOrderValue)
          : null,
        terms: couponForm.terms?.trim() || "",
        applicableItems: couponForm.applicableItems
          ? couponForm.applicableItems
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        type: couponForm.type,
        isActive: couponForm.isActive,
      };

      if (!isEditing) {
        payload.couponCode = couponForm.couponCode.trim().toUpperCase();
      }

      let url;
      let method;

      if (isEditing) {
        url = `${API_BASE_URL}/coupons/${selectedCoupon._id}`;
        method = "PATCH";
      } else {
        url = `${API_BASE_URL}/coupons`;
        method = "POST";
      }

      const response = await fetch(url, {
        method,
        headers: getAuthHeader(),
        body: JSON.stringify(payload),
      });

      const responseData = await parseApiResponse(response);

      if (!response.ok) {
        throw new Error(responseData?.message || "Failed to save coupon");
      }

      const successMessage = isEditing
        ? "Coupon updated successfully!"
        : "Coupon created successfully!";

      setSuccess(successMessage);
      setShowModal(false);
      setFormErrors({});
      setImageFile(null);
      setCoverImageFile(null);
      setImagePreview(null);
      setCoverImagePreview(null);
      fetchCoupons();
    } catch (err) {
      const fallback = "Failed to save coupon";
      const message =
        typeof err?.message === "string"
          ? err.message
          : Array.isArray(err?.message)
            ? err.message.join(", ")
            : fallback;
      setError(message || fallback);
    } finally {
      setActionLoading(false);
      setUploadingImage(false);
      setUploadingCoverImage(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDeleteCoupon = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/coupons/${couponToDelete}`,
        {
          method: "DELETE",
          headers: getAuthHeader(),
        },
      );

      const responseData = await parseApiResponse(response);

      if (!response.ok) {
        throw new Error(responseData?.message || "Delete failed");
      }

      setSuccess("Coupon deleted!");
      setShowDeleteCouponConfirm(false);
      fetchCoupons();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();
      setCoupons((prevCoupons) =>
        prevCoupons.filter((coupon) => {
          if (coupon?.isDeleted === true || coupon?.deletedAt != null) {
            return false;
          }
          if (!coupon?.expiresAt) {
            return true;
          }
          const expiryTime = new Date(coupon.expiresAt).getTime();
          return Number.isFinite(expiryTime) ? expiryTime > now : true;
        }),
      );
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div
      className={`min-h-screen p-4 sm:p-6 md:p-8 space-y-6 ${
        isDarkMode
          ? "bg-gradient-to-br from-black via-zinc-950 to-black text-white"
          : "bg-gray-100 text-gray-900"
      }`}
    >
      {error && (
        <div className={`p-4 rounded-xl flex items-start gap-3 border ${isDarkMode ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-red-50 border-red-200 text-red-600"}`}>
          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1 text-sm sm:text-base">{error}</span>
          <button onClick={() => setError(null)} className="flex-shrink-0">
            <X size={18} />
          </button>
        </div>
      )}

      {success && (
        <div className={`p-4 rounded-xl flex items-start gap-3 border ${isDarkMode ? "bg-green-500/10 border-green-500/30 text-green-400" : "bg-green-50 border-green-200 text-green-600"}`}>
          <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1 text-sm sm:text-base">{success}</span>
          <button onClick={() => setSuccess(null)} className="flex-shrink-0">
            <X size={18} />
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-400 to-orange-300 bg-clip-text text-transparent">
            Coupons Management
          </h1>
          <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {coupons.length} coupon{coupons.length !== 1 ? "s" : ""} available
          </p>
        </div>

        {userRole === "admin" && (
          <button
            onClick={openCreateModal}
            className="flex items-center justify-center sm:justify-start gap-2 px-4 py-2 bg-gradient-to-r from-orange-400 to-orange-500 text-black font-semibold rounded-lg hover:from-orange-500 hover:to-orange-600 transition-all shadow-lg hover:shadow-orange-500/20 w-full sm:w-auto"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Create Coupon</span>
            <span className="sm:hidden">Add</span>
          </button>
        )}
      </div>

      <div className={`rounded-2xl p-4 sm:p-6 md:p-8 backdrop-blur-sm border ${isDarkMode ? "bg-zinc-900/40 border-zinc-800/50" : "bg-white border-gray-200 shadow-sm"}`}>
        {couponsLoading ? (
          <div className="flex justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <Loader className="animate-spin text-orange-400" size={32} />
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Loading coupons...</p>
            </div>
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-16">
            <Tag size={48} className={`mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`} />
            <p className={`text-lg ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>No coupons found</p>
            {userRole === "admin" && (
              <button
                onClick={openCreateModal}
                className="mt-4 px-4 py-2 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors"
              >
                Create your first coupon
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {coupons.map((coupon) => (
              <div
                key={coupon._id}
                className={`group border rounded-xl overflow-hidden relative transition-all ${
                  isDarkMode
                    ? "bg-zinc-800/30 border-zinc-700/50 hover:border-orange-400/30"
                    : "bg-white border-gray-200 hover:border-orange-400"
                }`}
              >
                {coupon.coverImage && (
                  <div className={`relative overflow-hidden ${isDarkMode ? "bg-zinc-700" : "bg-gray-200"}`}>
                    <img
                      src={coupon.coverImage}
                      className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105"
                      alt={coupon.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}

                <div className="p-4">
                  {coupon.couponCode && (
                    <div className={`inline-block mb-2 px-2.5 py-1 rounded text-xs font-semibold tracking-wide ${isDarkMode ? "bg-orange-500/15 border border-orange-400/30 text-orange-300" : "bg-orange-50 border border-orange-200 text-orange-600"}`}>
                      {coupon.couponCode.toUpperCase()}
                    </div>
                  )}
                  <h3 className={`font-bold text-base sm:text-lg line-clamp-2 group-hover:text-orange-400 transition-colors ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {coupon.title}
                  </h3>
                  <p className={`text-xs sm:text-sm line-clamp-2 mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {coupon.description}
                  </p>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <DollarSign
                        size={16}
                        className="text-orange-400/70 flex-shrink-0"
                      />
                      <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                        {coupon.priceInTokens}
                        <span className={`text-xs ml-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                          tokens
                        </span>
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock
                        size={16}
                        className="text-blue-400/70 flex-shrink-0"
                      />
                      <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                        {new Date(coupon.expiresAt).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                          },
                        )}
                      </span>
                    </div>
                  </div>

                  {coupon.type && (
                    <div className={`mt-3 inline-block px-2 py-1 rounded text-xs font-medium ${isDarkMode ? "bg-zinc-700/50 text-orange-400/80" : "bg-orange-50 text-orange-500"}`}>
                      {coupon.type.charAt(0).toUpperCase() +
                        coupon.type.slice(1)}
                    </div>
                  )}

                  <div className={`flex gap-2 mt-4 pt-4 border-t ${isDarkMode ? "border-zinc-700/30" : "border-gray-200"}`}>
                    <button
                      onClick={() => {
                        setSelectedCoupon(coupon);
                        setShowCouponDetailModal(true);
                      }}
                      className={`flex-1 p-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm ${isDarkMode ? "bg-zinc-700/40 hover:bg-zinc-600/60 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
                      title="View details"
                    >
                      <Eye size={16} />
                      <span>View</span>
                    </button>

                    {userRole === "admin" && (
                      <>
                        <button
                          onClick={() => openEditModal(coupon)}
                          className={`flex-1 p-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm ${isDarkMode ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" : "bg-blue-50 text-blue-600 hover:bg-blue-100"}`}
                          title="Edit coupon"
                        >
                          <Edit2 size={16} />
                          <span>Edit</span>
                        </button>

                        <button
                          onClick={() => {
                            setCouponToDelete(coupon._id);
                            setShowDeleteCouponConfirm(true);
                          }}
                          className={`flex-1 p-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm ${isDarkMode ? "bg-red-500/20 text-red-400 hover:bg-red-500/30" : "bg-red-50 text-red-600 hover:bg-red-100"}`}
                          title="Delete coupon"
                        >
                          <Trash2 size={16} />
                          <span>Delete</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDeleteCouponConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`rounded-2xl w-full max-w-sm border ${isDarkMode ? "bg-gradient-to-br from-zinc-900 to-black border-zinc-800" : "bg-white border-gray-200 shadow-xl"}`}>
            <div className={`p-6 border-b ${isDarkMode ? "border-zinc-800" : "border-gray-200"}`}>
              <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Delete Coupon?</h2>
              <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                This action cannot be undone.
              </p>
            </div>
            <div className="p-6">
              <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                Are you sure you want to permanently delete this coupon?
              </p>
            </div>
            <div className={`p-6 border-t flex justify-end gap-3 ${isDarkMode ? "border-zinc-800" : "border-gray-200"}`}>
              <button
                onClick={() => setShowDeleteCouponConfirm(false)}
                className={`px-4 py-2 rounded-lg transition-colors font-medium ${isDarkMode ? "bg-zinc-800 hover:bg-zinc-700 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCoupon}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white font-medium transition-colors disabled:opacity-70"
              >
                {actionLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCouponDetailModal && selectedCoupon && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border ${isDarkMode ? "bg-gradient-to-br from-zinc-900 to-black border-zinc-800" : "bg-white border-gray-200 shadow-xl"}`}>
            <div className={`p-6 border-b flex justify-between items-start gap-4 sticky top-0 backdrop-blur z-10 ${isDarkMode ? "border-zinc-800 bg-black/50" : "border-gray-200 bg-white/80"}`}>
              <div>
                <h2 className="text-2xl font-bold text-orange-400">
                  {selectedCoupon.title}
                </h2>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {selectedCoupon.type}
                </p>
              </div>
              <button
                onClick={() => setShowCouponDetailModal(false)}
                className={`p-1 rounded-lg transition-colors flex-shrink-0 ${isDarkMode ? "hover:bg-zinc-800" : "hover:bg-gray-100"}`}
              >
                <X size={24} className={isDarkMode ? "text-gray-400" : "text-gray-500"} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {selectedCoupon.coverImage && (
                <img
                  src={selectedCoupon.coverImage}
                  className={`w-full h-56 object-cover rounded-xl border ${isDarkMode ? "border-zinc-700" : "border-gray-200"}`}
                  alt={selectedCoupon.title}
                />
              )}

              {selectedCoupon.couponCode && (
                <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-orange-500/10 border-orange-500/20" : "bg-orange-50 border-orange-200"}`}>
                  <p className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Coupon Code</p>
                  <p className={`font-semibold text-lg tracking-wide ${isDarkMode ? "text-orange-300" : "text-orange-600"}`}>
                    {selectedCoupon.couponCode.toUpperCase()}
                  </p>
                </div>
              )}

              <p className={`leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                {selectedCoupon.description}
              </p>

              <div className={`grid grid-cols-2 gap-4 p-4 rounded-xl border ${isDarkMode ? "bg-zinc-800/20 border-zinc-700/30" : "bg-gray-50 border-gray-200"}`}>
                <div>
                  <span className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Price
                  </span>
                  <span className="text-orange-400 font-bold text-lg">
                    {selectedCoupon.priceInTokens} tokens
                  </span>
                </div>

                <div>
                  <span className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Discount
                  </span>
                  <span className={`font-bold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {selectedCoupon.discountValue}
                    {selectedCoupon.discountType === "percentage" ? "%" : ""}
                  </span>
                </div>

                <div>
                  <span className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Expires
                  </span>
                  <span className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {new Date(selectedCoupon.expiresAt).toLocaleDateString()}
                  </span>
                </div>

                <div>
                  <span className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Status
                  </span>
                  <span
                    className={`font-semibold ${
                      selectedCoupon.isActive
                        ? "text-green-400"
                        : isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {selectedCoupon.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              {selectedCoupon.minOrderValue && (
                <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-blue-500/10 border-blue-500/20" : "bg-blue-50 border-blue-200"}`}>
                  <p className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Min Order Value</p>
                  <p className={`font-semibold ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                    ${selectedCoupon.minOrderValue}
                  </p>
                </div>
              )}

              {selectedCoupon.applicableItems &&
                selectedCoupon.applicableItems.length > 0 && (
                  <div>
                    <h3 className={`font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                      Applicable Items
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedCoupon.applicableItems.map((item, idx) => (
                        <span
                          key={idx}
                          className={`px-3 py-1 rounded-full text-sm border ${isDarkMode ? "bg-orange-400/10 border-orange-400/30 text-orange-400" : "bg-orange-50 border-orange-200 text-orange-600"}`}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              {selectedCoupon.terms && (
                <div>
                  <h3 className={`font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Terms & Conditions
                  </h3>
                  <p className={`text-sm p-4 rounded-lg border ${isDarkMode ? "text-gray-400 bg-zinc-800/20 border-zinc-700/30" : "text-gray-500 bg-gray-50 border-gray-200"}`}>
                    {selectedCoupon.terms}
                  </p>
                </div>
              )}
            </div>

            <div className={`p-6 border-t flex justify-end sticky bottom-0 backdrop-blur ${isDarkMode ? "border-zinc-800 bg-black/50" : "border-gray-200 bg-white/80"}`}>
              <button
                onClick={() => setShowCouponDetailModal(false)}
                className="px-6 py-2 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border ${isDarkMode ? "bg-gradient-to-br from-zinc-900 to-black border-zinc-800" : "bg-white border-gray-200 shadow-xl"}`}>
            <div className={`p-6 border-b flex justify-between items-center sticky top-0 backdrop-blur z-10 ${isDarkMode ? "border-zinc-800 bg-black/50" : "border-gray-200 bg-white/80"}`}>
              <h2 className="text-2xl font-bold text-orange-400">
                {isEditing ? "Edit Coupon" : "Create New Coupon"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className={`p-1 rounded-lg transition-colors ${isDarkMode ? "hover:bg-zinc-800" : "hover:bg-gray-100"}`}
              >
                <X size={24} className={isDarkMode ? "text-gray-400" : "text-gray-500"} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-6">
              {!isEditing && (
                <div className="md:col-span-2">
                  <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Coupon Code *
                  </label>
                  <input
                    type="text"
                    value={couponForm.couponCode}
                    onChange={(e) =>
                      setCouponForm({
                        ...couponForm,
                        couponCode: e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9_-]/g, "")
                          .slice(0, 30),
                      })
                    }
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors ${isDarkMode ? "bg-zinc-800/50 text-white placeholder-gray-500" : "bg-gray-50 text-gray-900 placeholder-gray-400"} ${
                      formErrors.couponCode
                        ? "border-red-500/50 focus:border-red-400/50"
                        : isDarkMode ? "border-zinc-700/50 focus:border-orange-400/50" : "border-gray-300 focus:border-orange-400"
                    }`}
                    placeholder="e.g. WELCOME20"
                    maxLength={30}
                    title="Enter coupon code"
                  />
                  {formErrors.couponCode && (
                    <p className="text-red-400 text-xs mt-1">
                      {formErrors.couponCode}
                    </p>
                  )}
                </div>
              )}

              <div className="md:col-span-2">
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Title *
                </label>
                <input
                  type="text"
                  value={couponForm.title}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, title: e.target.value })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors ${isDarkMode ? "bg-zinc-800/50 text-white placeholder-gray-500" : "bg-gray-50 text-gray-900 placeholder-gray-400"} ${
                    formErrors.title
                      ? "border-red-500/50 focus:border-red-400/50"
                      : isDarkMode ? "border-zinc-700/50 focus:border-orange-400/50" : "border-gray-300 focus:border-orange-400"
                  }`}
                  placeholder="20% Off All Items"
                />
                {formErrors.title && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.title}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Description *
                </label>
                <textarea
                  value={couponForm.description}
                  onChange={(e) =>
                    setCouponForm({
                      ...couponForm,
                      description: e.target.value,
                    })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors h-20 resize-none ${isDarkMode ? "bg-zinc-800/50 text-white placeholder-gray-500" : "bg-gray-50 text-gray-900 placeholder-gray-400"} ${
                    formErrors.description
                      ? "border-red-500/50 focus:border-red-400/50"
                      : isDarkMode ? "border-zinc-700/50 focus:border-orange-400/50" : "border-gray-300 focus:border-orange-400"
                  }`}
                  placeholder="Get 20% discount on all purchases"
                />
                {formErrors.description && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.description}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Image *
                </label>
                <label
                  className={`flex flex-col items-center justify-center w-full p-6 rounded-lg border-2 border-dashed cursor-pointer transition-all ${
                    formErrors.imageUrl
                      ? "border-red-500/50"
                      : isDarkMode ? "border-zinc-700/50 hover:border-orange-400/50" : "border-gray-300 hover:border-orange-400"
                  }`}
                >
                  <Upload className={`w-8 h-8 mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`} />
                  <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                    Click to upload image
                  </span>
                  <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    PNG, JPG, WEBP up to 5MB
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "image")}
                    className="hidden"
                  />
                </label>
                {(imagePreview || couponForm.imageUrl) && (
                  <img
                    src={imagePreview || couponForm.imageUrl}
                    alt="Coupon"
                    className={`mt-3 w-full h-32 object-cover rounded-lg border ${isDarkMode ? "border-zinc-700/50" : "border-gray-200"}`}
                  />
                )}
                {formErrors.imageUrl && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.imageUrl}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Cover Image *
                </label>
                <label
                  className={`flex flex-col items-center justify-center w-full p-6 rounded-lg border-2 border-dashed cursor-pointer transition-all ${
                    formErrors.coverImage
                      ? "border-red-500/50"
                      : isDarkMode ? "border-zinc-700/50 hover:border-orange-400/50" : "border-gray-300 hover:border-orange-400"
                  }`}
                >
                  <Upload className={`w-8 h-8 mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`} />
                  <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                    Click to upload cover image
                  </span>
                  <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    PNG, JPG, WEBP up to 5MB
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "cover")}
                    className="hidden"
                  />
                </label>
                {(coverImagePreview || couponForm.coverImage) && (
                  <img
                    src={coverImagePreview || couponForm.coverImage}
                    alt="Coupon cover"
                    className={`mt-3 w-full h-40 object-cover rounded-lg border ${isDarkMode ? "border-zinc-700/50" : "border-gray-200"}`}
                  />
                )}
                {formErrors.coverImage && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.coverImage}
                  </p>
                )}
              </div>

              <div>
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Discount Value *{" "}
                  {couponForm.discountType === "percentage" && "(Max 100%)"}
                </label>
                <input
                  type="number"
                  value={couponForm.discountValue}
                  onChange={(e) =>
                    setCouponForm({
                      ...couponForm,
                      discountValue: e.target.value,
                    })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors ${isDarkMode ? "bg-zinc-800/50 text-white placeholder-gray-500" : "bg-gray-50 text-gray-900 placeholder-gray-400"} ${
                    formErrors.discountValue
                      ? "border-red-500/50 focus:border-red-400/50"
                      : isDarkMode ? "border-zinc-700/50 focus:border-orange-400/50" : "border-gray-300 focus:border-orange-400"
                  }`}
                  placeholder="10"
                  min="1"
                />
                {formErrors.discountValue && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.discountValue}
                  </p>
                )}
              </div>

              <div>
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Discount Type
                </label>
                <select
                  value={couponForm.discountType}
                  onChange={(e) =>
                    setCouponForm({
                      ...couponForm,
                      discountType: e.target.value,
                    })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors ${isDarkMode ? "bg-zinc-800/50 border-zinc-700/50 text-white focus:border-orange-400/50" : "bg-gray-50 border-gray-300 text-gray-900 focus:border-orange-400"}`}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
                {formErrors.discountType && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.discountType}
                  </p>
                )}
              </div>

              <div>
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Price (Tokens) *
                </label>
                <input
                  type="number"
                  value={couponForm.priceInTokens}
                  onChange={(e) =>
                    setCouponForm({
                      ...couponForm,
                      priceInTokens: e.target.value,
                    })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors ${isDarkMode ? "bg-zinc-800/50 text-white placeholder-gray-500" : "bg-gray-50 text-gray-900 placeholder-gray-400"} ${
                    formErrors.priceInTokens
                      ? "border-red-500/50 focus:border-red-400/50"
                      : isDarkMode ? "border-zinc-700/50 focus:border-orange-400/50" : "border-gray-300 focus:border-orange-400"
                  }`}
                  placeholder="1"
                  min="1"
                />
                {formErrors.priceInTokens && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.priceInTokens}
                  </p>
                )}
              </div>

              <div>
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Expires At *
                </label>
                <ExpiresAtPicker
                  value={couponForm.expiresAt ?? ""}
                  onChange={(next) =>
                    setCouponForm({ ...couponForm, expiresAt: next })
                  }
                  error={!!formErrors.expiresAt}
                />
                {formErrors.expiresAt && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.expiresAt}
                  </p>
                )}
              </div>

              <div>
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Min Order Value
                </label>
                <input
                  type="number"
                  value={couponForm.minOrderValue}
                  onChange={(e) =>
                    setCouponForm({
                      ...couponForm,
                      minOrderValue: e.target.value,
                    })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors ${isDarkMode ? "bg-zinc-800/50 text-white placeholder-gray-500" : "bg-gray-50 text-gray-900 placeholder-gray-400"} ${
                    formErrors.minOrderValue
                      ? "border-red-500/50 focus:border-red-400/50"
                      : isDarkMode ? "border-zinc-700/50 focus:border-orange-400/50" : "border-gray-300 focus:border-orange-400"
                  }`}
                  placeholder="0 (optional)"
                  min="0"
                />
                {formErrors.minOrderValue && (
                  <p className="text-red-400 text-xs mt-1">
                    {formErrors.minOrderValue}
                  </p>
                )}
              </div>

              <div>
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Coupon Type
                </label>
                <select
                  value={couponForm.type}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, type: e.target.value })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors ${isDarkMode ? "bg-zinc-800/50 border-zinc-700/50 text-white focus:border-orange-400/50" : "bg-gray-50 border-gray-300 text-gray-900 focus:border-orange-400"}`}
                >
                  <option value="discount">Discount</option>
                  <option value="cashback">Cashback</option>
                  <option value="gift">Gift</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Applicable Items (comma separated)
                </label>
                <input
                  type="text"
                  value={couponForm.applicableItems}
                  onChange={(e) =>
                    setCouponForm({
                      ...couponForm,
                      applicableItems: e.target.value,
                    })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors ${isDarkMode ? "bg-zinc-800/50 border-zinc-700/50 text-white placeholder-gray-500 focus:border-orange-400/50" : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-orange-400"}`}
                  placeholder="Item1, Item2, Item3 (optional)"
                />
              </div>

              <div className="md:col-span-2">
                <label className={`text-sm font-semibold block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Terms & Conditions
                </label>
                <textarea
                  value={couponForm.terms}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, terms: e.target.value })
                  }
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none transition-colors h-20 resize-none ${isDarkMode ? "bg-zinc-800/50 border-zinc-700/50 text-white placeholder-gray-500 focus:border-orange-400/50" : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-orange-400"}`}
                  placeholder="Add any terms or conditions (optional)"
                />
              </div>

              <div className={`md:col-span-2 flex items-center gap-3 p-4 rounded-lg border ${isDarkMode ? "bg-zinc-800/20 border-zinc-700/30" : "bg-gray-50 border-gray-200"}`}>
                <input
                  type="checkbox"
                  id="isActive"
                  checked={couponForm.isActive}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, isActive: e.target.checked })
                  }
                  className="w-5 h-5 cursor-pointer"
                />
                <label
                  htmlFor="isActive"
                  className={`text-sm font-semibold cursor-pointer ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Active Coupon
                </label>
              </div>
            </div>

            <div className={`p-6 border-t flex justify-end gap-3 sticky bottom-0 backdrop-blur ${isDarkMode ? "border-zinc-800 bg-black/50" : "border-gray-200 bg-white/80"}`}>
              <button
                onClick={() => setShowModal(false)}
                className={`px-6 py-2 rounded-lg transition-colors font-medium ${isDarkMode ? "bg-zinc-800 hover:bg-zinc-700 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCoupon}
                disabled={actionLoading || uploadingImage || uploadingCoverImage}
                className="px-6 py-2 bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-black font-bold rounded-lg transition-all disabled:opacity-70"
              >
                {actionLoading || uploadingImage || uploadingCoverImage
                  ? uploadingImage || uploadingCoverImage
                    ? "Uploading..."
                    : "Saving..."
                  : isEditing
                    ? "Update Coupon"
                    : "Create Coupon"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsManagement;
